import _ from "lodash";

function modulus(divident, divisor) {
  return ((divident % divisor) + divisor) % divisor;
}

function remove_prefix(string, prefix) {
  return string && string.startsWith(prefix)
    ? string.slice(prefix.length)
    : string;
}

function trimPageData(string) {
  var trim = "page-data-";
  return string.startsWith(trim) ? string.slice(trim.length) : string;
}

function trimProjectMeta(string) {
  var trim = "project-meta-";
  return string.startsWith(trim) ? string.slice(trim.length) : string;
}

function trimPageContent(string) {
  var trim = "page-content-";
  return string.startsWith(trim) ? string.slice(trim.length) : string;
}

function makeID(length) {
  var result = "";
  var characters = "abcdefghijklmnopqrstuvwxyz0123456789";
  var charactersLength = characters.length;
  for (var i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

function isEmptyObject(obj) {
  return (
    obj &&
    Object.keys(obj).length === 0 &&
    Object.getPrototypeOf(obj) === Object.prototype
  );
}

function emptyPage() {
  return '{"ROOT":{"type":{"resolvedName":"Page"},"isCanvas":true,"props":{},"displayName":"Page","custom":{"name":"Page","slug":"grigora/page"},"hidden":false,"nodes":[],"linkedNodes":{}}}';
}

function limitTextSize(text, length = 20, suffix = "…") {
  if (!text) {
    return "";
  }
  if (text.length < length + 1) {
    return text;
  }
  return text.slice(0, length + 1) + suffix;
}

function emptyCMSPost() {
  return `{
    "ROOT": {
      "type": {
        "resolvedName": "Page"
      },
      "isCanvas": true,
      "props": {
        "padding": 5,
        "data-cy": "root-page"
      },
      "displayName": "Page",
      "custom": {
        "name": "Page",
        "slug": "grigora/page"
      },
      "hidden": false,
      "nodes": [
        "JogOishgcF"
      ],
      "linkedNodes": {}
    },
    "JogOishgcF": {
      "type": {
        "resolvedName": "Text"
      },
      "isCanvas": false,
      "props": {
        "content": ""
      },
      "displayName": "Text",
      "custom": {
        "name": "Text",
        "slug": "grigora/text"
      },
      "parent": "ROOT",
      "hidden": false,
      "nodes": [],
      "linkedNodes": {}
    }
  }`;
}

function sameDefaultValueCheck(value) {
  if (value === undefined || value === null || !_.isObject(value)) {
    return true;
  }

  const values = Object.values(value ?? {});

  // If empty or single value, return true
  if (values?.length <= 1) {
    return true;
  }

  // Compare all values with the first value using isEqual
  const firstValue = values?.[0];
  return values?.every((v) => _.isEqual(v, firstValue));
}

/**
 * Normalizes a given string of HTML to remove the Windows-specific "Fragment"
 * comments and any preceding and trailing content.
 *
 * @param {string} html the html to be normalized
 * @return {string} the normalized html
 */
function removeWindowsFragments(html) {
  const startStr = "<!--StartFragment-->";
  const startIdx = html.indexOf(startStr);
  if (startIdx > -1) {
    html = html.substring(startIdx + startStr.length);
  } else {
    // No point looking for EndFragment
    return html;
  }

  const endStr = "<!--EndFragment-->";
  const endIdx = html.indexOf(endStr);
  if (endIdx > -1) {
    html = html.substring(0, endIdx);
  }

  return html;
}

/**
 * Removes the charset meta tag inserted by Chromium.
 * See:
 * - https://github.com/WordPress/gutenberg/issues/33585
 * - https://bugs.chromium.org/p/chromium/issues/detail?id=1264616#c4
 *
 * @param {string} html the html to be stripped of the meta tag.
 * @return {string} the cleaned html
 */
function removeCharsetMetaTag(html) {
  const metaTag = `<meta charset='utf-8'>`;

  if (html.startsWith(metaTag)) {
    return html.slice(metaTag.length);
  }

  return html;
}

/**
 * Gets all files from a DataTransfer object.
 *
 * @param {DataTransfer} dataTransfer DataTransfer object to inspect.
 *
 * @return {File[]} An array containing all files.
 */
function getFilesFromDataTransfer(dataTransfer) {
  const files = Array.from(dataTransfer.files);

  Array.from(dataTransfer.items).forEach((item) => {
    const file = item.getAsFile();

    if (
      file &&
      !files.find(
        ({ name, type, size }) =>
          name === file.name && type === file.type && size === file.size,
      )
    ) {
      files.push(file);
    }
  });

  return files;
}

/**
 * Handle the case where pasted clipboard has screenshot image file generated by the
 * softwares like MS Docs.
 */
export function shouldDismissPastedFiles(files, html) {
  if (html && files?.length === 1 && files[0].type.indexOf("image/") === 0) {
    // A single <img> tag found in the HTML source suggests that the
    // content being pasted revolves around an image. Sometimes there are
    // other elements found, like <figure>, but we assume that the user's
    // intention is to paste the actual image file.
    const IMAGE_TAG = /<\s*img\b/gi;

    // Even when there is exactly one <img> tag in the HTML payload, we
    // choose to weed out local images, i.e. those whose source starts with
    // "file://". These payloads occur in specific configurations, such as
    // when copying an entire document from Microsoft Word, that contains
    // text and exactly one image, and pasting that content using Google
    // Chrome.
    const IMG_WITH_LOCAL_SRC = /<\s*img\b[^>]*\bsrc="file:\/\//i;

    if (html.match(IMAGE_TAG)?.length !== 1 || html.match(IMG_WITH_LOCAL_SRC))
      return true;
  }

  return false;
}

function isJsonString(str) {
  if (str === null || str === undefined) {
    return false;
  }

  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

const fromEntries = (pairs) => {
  if (Object.fromEntries) {
    return Object.fromEntries(pairs);
  }
  return pairs.reduce(
    (accum, [id, value]) => ({
      ...accum,
      [id]: value,
    }),
    {},
  );
};

function removeTrailingSlash(str) {
  return str ? str.replace(/\/+$/, "") : str;
}

const segmenter =
  Intl.Segmenter && new Intl.Segmenter("en", { granularity: "sentence" });

function getSentencesFromText(str) {
  if (!str) {
    return [];
  }

  // Intl Segmenter is not available in Firefox
  if (segmenter) {
    return Array.from(segmenter.segment(str), (s) => s.segment);
  }

  return str.match(/[^\.!\?]+[\.!\?]+/g) ?? [];
}

function getDirectoryData(
  allDirectories,
  allDirectoryTypes,
  directoryId,
  index,
  field,
) {
  var directory = allDirectoryTypes?.find?.(
    (directory) =>
      remove_prefix(directory?.sk, "directory-type-") ===
      remove_prefix(directoryId, "directory-type-"),
  );

  if (!directory) {
    return "";
  }

  var listing = allDirectories?.[directoryId]?.[index];
  if (!listing) {
    return "";
  }

  var fieldData = listing.fields?.[field];
  return fieldData ? fieldData : "";
}

function getDirectoryListing(allDirectories, directoryId, listingId) {
  var listing = allDirectories?.[directoryId]?.listings?.find(
    (listing) =>
      remove_prefix(
        listing.sk,
        "directory-listing-" + directoryId + "-list-",
      ) === listingId,
  );
  if (!listing) {
    return "";
  }

  return listing;
}

function getDirectoryListingIndex(allDirectories, directoryId, listingId) {
  var allListings = allDirectories?.[directoryId];
  var listing = allListings?.findIndex(
    (listing) =>
      remove_prefix(
        listing.sk,
        "directory-listing-" + directoryId + "-list-",
      ) === listingId,
  );
  if (listing === -1) {
    return -1;
  }

  return listing;
}

function getDirectoryField(allDirectories, directoryId, index, directoryField) {
  // Key value from allDirectories
  allDirectories = allDirectories?.reduce((acc, curr) => {
    acc[remove_prefix(curr.sk, "directory-type-")] = curr;
    return acc;
  }, {});

  return allDirectories?.[directoryId]?.fields?.find(
    (field) => field.id === directoryField,
  );
}

function getDirectoryReferenceData(
  directory,
  allDirectories,
  allDirectoryTypes,
  directoryId,
  index,
  directoryField,
) {
  if (!directoryField?.includes(".")) {
    if (directoryField?.includes?.("current-listing")) {
      directoryField = "slug";
    }

    return {
      dynamicValue: directory?.[index ?? 0]?.fields?.[directoryField],
      fieldSchema: allDirectoryTypes
        ?.find?.(
          (directory) =>
            remove_prefix(directory?.sk, "directory-type-") ===
            remove_prefix(directoryId, "directory-type-"),
        )
        ?.fields?.find((field) => field.id === directoryField),
      referenceDirectory: allDirectoryTypes?.find?.(
        (directory) =>
          remove_prefix(directory?.sk, "directory-type-") ===
          remove_prefix(directoryId, "directory-type-"),
      ),
    };
  }

  var [currentFieldId, targetFieldId] = directoryField.split(".");

  if (targetFieldId.includes("current-listing")) {
    targetFieldId = "slug";
  }

  var referenceDirectoryType = allDirectoryTypes?.find?.(
    (directory) =>
      remove_prefix(directory?.sk, "directory-type-") ===
      remove_prefix(directoryId, "directory-type-"),
  );

  const referenceDirectoryTypeId = referenceDirectoryType?.fields?.find(
    (field) => field.id === currentFieldId,
  )?.referenceTypeId;

  const listingId = getDirectoryData(
    allDirectories,
    allDirectoryTypes,
    directoryId,
    index,
    currentFieldId,
  );

  const indexOfTargetListing = getDirectoryListingIndex(
    allDirectories,
    referenceDirectoryTypeId,
    listingId,
  );

  const referenceValue =
    allDirectories?.[referenceDirectoryTypeId]?.[indexOfTargetListing]
      ?.fields?.[targetFieldId];

  if (!referenceValue) {
    return { dynamicValue: "", fieldSchema: null };
  }

  return {
    dynamicValue: referenceValue,
    fieldSchema: allDirectoryTypes
      ?.find?.(
        (directory) =>
          remove_prefix(directory?.sk, "directory-type-") ===
          remove_prefix(referenceDirectoryTypeId, "directory-type-"),
      )
      ?.fields?.find((field) => field.id === targetFieldId),
    referenceDirectory: allDirectoryTypes?.find?.(
      (directory) =>
        remove_prefix(directory?.sk, "directory-type-") ===
        remove_prefix(referenceDirectoryTypeId, "directory-type-"),
    ),
  };
}

export {
  remove_prefix,
  modulus,
  trimPageData,
  trimProjectMeta,
  trimPageContent,
  makeID,
  emptyPage,
  isEmptyObject,
  emptyCMSPost,
  sameDefaultValueCheck,
  removeWindowsFragments,
  removeCharsetMetaTag,
  getFilesFromDataTransfer,
  isJsonString,
  fromEntries,
  removeTrailingSlash,
  limitTextSize,
  getSentencesFromText,
  getDirectoryField,
  getDirectoryReferenceData,
};
